import React, { useState } from 'react';
import { 
  Paper, 
  InputBase, 
  IconButton, 
  Divider,
  Box,
  Tooltip
} from '@mui/material';
import { 
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

/**
 * Reusable search bar component with optional filter button
 * 
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {function} props.onSearch - Function to call when search is submitted
 * @param {function} props.onChange - Function to call when search text changes
 * @param {string} props.value - Controlled value for the search input
 * @param {boolean} props.showFilter - Whether to show the filter button
 * @param {function} props.onFilterClick - Function to call when filter button is clicked
 * @param {Object} props.sx - Additional styles
 */
const SearchBar = ({
  placeholder = 'Search...',
  onSearch,
  onChange,
  value: externalValue,
  showFilter = false,
  onFilterClick,
  sx = {}
}) => {
  // Use internal state if not controlled externally
  const [internalValue, setInternalValue] = useState('');
  
  // Determine if component is controlled or uncontrolled
  const isControlled = externalValue !== undefined;
  const value = isControlled ? externalValue : internalValue;
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    if (!isControlled) {
      setInternalValue(newValue);
    }
    
    if (onChange) {
      onChange(newValue);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(value);
    }
  };
  
  const handleClear = () => {
    if (!isControlled) {
      setInternalValue('');
    }
    
    if (onChange) {
      onChange('');
    }
    
    if (onSearch) {
      onSearch('');
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={0}
      variant="outlined"
      sx={{
        p: '2px 4px',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 2,
        ...sx
      }}
    >
      <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
        <SearchIcon />
      </IconButton>
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        value={value}
        onChange={handleChange}
      />
      {value && (
        <IconButton 
          sx={{ p: '10px' }} 
          aria-label="clear search" 
          onClick={handleClear}
        >
          <ClearIcon />
        </IconButton>
      )}
      
      {showFilter && (
        <>
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <Tooltip title="Show filters">
            <IconButton 
              color="primary" 
              sx={{ p: '10px' }} 
              aria-label="filters"
              onClick={onFilterClick}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Paper>
  );
};

export default SearchBar;
